import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { map, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { AiSettingsService } from './ai-settings.service';
import { DockModeService } from './dock-mode.service';
import { DEFAULT_DOCK_AI_SNAPSHOT_PROMPT } from '../constants/ai-snapshot-prompt';
import 'rxjs/add/observable/throw';

interface PreparedImage {
  dataUrl: string;
  width: number;
  height: number;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 24;

// Flux Kontext only generates a fixed set of aspect ratios. With
// aspect_ratio: 'match_input_image' an input of any OTHER ratio (our canvas
// is whatever the browser window happens to be) gets letterboxed by the
// model, and the white bars come back baked into the result. So the prepared
// image is center-cropped to the nearest supported ratio instead; the sizes
// below are exact-ratio pairs, every side a multiple of 32, long side within
// the model's comfortable 1024-1536 range.
const SUPPORTED_SIZES: Array<{ width: number; height: number }> = [
  { width: 1344, height: 576 }, // 21:9
  { width: 1536, height: 768 }, // 2:1
  { width: 1536, height: 864 }, // 16:9
  { width: 1536, height: 1024 }, // 3:2
  { width: 1280, height: 960 }, // 4:3
  { width: 1280, height: 1024 }, // 5:4
  { width: 1280, height: 1280 }, // 1:1
  { width: 1024, height: 1280 }, // 4:5
  { width: 960, height: 1280 }, // 3:4
  { width: 1024, height: 1536 }, // 2:3
  { width: 864, height: 1536 }, // 9:16
  { width: 768, height: 1536 }, // 1:2
  { width: 576, height: 1344 } // 9:21
];

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class ReplicateAiImageService {
  private readonly apiBase = `${environment.apiUrl.replace(/\/$/, '')}/replicate`;
  private readonly upscaleModel = environment.replicateUpscaleModel;
  private cachedUpscaleVersionId: string | null = environment.replicateUpscaleModelVersion || null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private aiSettingsService: AiSettingsService,
    private dockModeService: DockModeService
  ) {}

  generateRealisticImage(imageUrl: string): Observable<string> {
    const accessToken = this.authService.getToken();
    if (!accessToken) {
      return Observable.throw(new Error('Authorization required'));
    }

    return this.aiSettingsService.getActiveConfig().pipe(
      switchMap(config => {
        // the backend-configured prompt is deck-oriented; dock projects get the
        // waterfront prompt until per-mode prompts exist server-side
        const prompt = this.dockModeService.isDockMode()
          ? DEFAULT_DOCK_AI_SNAPSHOT_PROMPT
          : config.prompt;
        return this.prepareImage(imageUrl).pipe(
          switchMap(prepared =>
            this.createFluxPrediction(prepared, prompt, config.model).pipe(
              switchMap((prediction: any) => this.resolvePredictionOutput(prediction)),
              switchMap((outputUrl: string) => this.upscaleImage(outputUrl))
            )
          )
        );
      }),
      switchMap((outputUrl: string) => this.fetchAsDataUrl(outputUrl))
    );
  }

  private createFluxPrediction(prepared: PreparedImage, prompt: string, model: string): Observable<any> {
    const [owner, name] = model.split('/');

    if (!owner || !name) {
      return Observable.throw(new Error('Invalid AI model configuration'));
    }

    // No 'Prefer: wait' on purpose: holding the connection while Replicate
    // generates races the nginx proxy timeout (~60s) in front of the API, and
    // a killed connection surfaces in the browser as a bogus CORS error
    // (nginx error pages carry no Access-Control-Allow-Origin). Create the
    // prediction immediately and let resolvePredictionOutput poll instead.
    return this.http.post(
      this.apiUrl(`/models/${owner}/${name}/predictions`),
      {
        input: {
          prompt: prompt,
          input_image: prepared.dataUrl,
          aspect_ratio: 'match_input_image',
          output_format: 'png',
          prompt_upsampling: true,
          safety_tolerance: 2
        }
      },
      httpOptions
    );
  }

  private apiUrl(path: string): string {
    const accessToken = this.authService.getToken();
    if (!accessToken) {
      throw new Error('Authorization required');
    }

    const normalizedPath = path.indexOf('/') === 0 ? path : `/${path}`;
    return `${this.apiBase}${normalizedPath}?access_token=${encodeURIComponent(accessToken)}`;
  }

  private prepareImage(imageUrl: string): Observable<PreparedImage> {
    if (!imageUrl) {
      return Observable.throw(new Error('No source image'));
    }

    const source$ = this.isDataUrl(imageUrl)
      ? of(imageUrl)
      : this.fetchAsDataUrl(this.normalizeImageUrl(imageUrl)).pipe(
          catchError(() => this.loadImageViaCrossOrigin(this.normalizeImageUrl(imageUrl)))
        );

    return source$.pipe(switchMap(dataUrl => this.renderPreparedImage(dataUrl)));
  }

  private isDataUrl(value: string): boolean {
    return !!value && value.indexOf('data:') === 0;
  }

  private normalizeImageUrl(imageUrl: string): string {
    if (this.isDataUrl(imageUrl)) {
      return imageUrl;
    }

    if (imageUrl.indexOf('http://') === 0 || imageUrl.indexOf('https://') === 0) {
      return imageUrl;
    }

    const base = environment.apiUrl.replace(/\/$/, '');
    return imageUrl.indexOf('/') === 0 ? `${base}${imageUrl}` : `${base}/${imageUrl}`;
  }

  private loadImageViaCrossOrigin(imageUrl: string): Observable<string> {
    return new Observable(observer => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            observer.error(new Error('Cannot read snapshot image'));
            return;
          }

          ctx.drawImage(img, 0, 0);
          observer.next(canvas.toDataURL('image/jpeg', 0.95));
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      };

      img.onerror = () => observer.error(new Error('Cannot load snapshot image'));
      img.src = imageUrl;
    });
  }

  private renderPreparedImage(imageUrl: string): Observable<PreparedImage> {
    return new Observable(observer => {
      const img = new Image();

      img.onload = () => {
        try {
          const size = this.pickSupportedSize(img.naturalWidth, img.naturalHeight);
          const canvas = document.createElement('canvas');
          canvas.width = size.width;
          canvas.height = size.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            observer.next({ dataUrl: imageUrl, width: size.width, height: size.height });
            observer.complete();
            return;
          }

          // center-crop the source to the target ratio — a few percent off
          // the long edges at worst, and the model gets nothing to letterbox
          const targetRatio = size.width / size.height;
          let sw = img.naturalWidth;
          let sh = img.naturalHeight;
          if (sw / sh > targetRatio) {
            sw = Math.round(sh * targetRatio);
          } else {
            sh = Math.round(sw / targetRatio);
          }
          const sx = Math.round((img.naturalWidth - sw) / 2);
          const sy = Math.round((img.naturalHeight - sh) / 2);

          ctx.imageSmoothingEnabled = true;
          (ctx as any).imageSmoothingQuality = 'high';
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size.width, size.height);
          observer.next({
            dataUrl: canvas.toDataURL('image/jpeg', 0.95),
            width: size.width,
            height: size.height
          });
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      };

      img.onerror = () => observer.error(new Error('Cannot prepare snapshot image'));
      img.src = imageUrl;
    });
  }

  private pickSupportedSize(width: number, height: number): { width: number; height: number } {
    const sourceRatio = width / Math.max(1, height);
    let best = SUPPORTED_SIZES[0];
    let bestDistance = Infinity;

    SUPPORTED_SIZES.forEach(size => {
      // log-space distance treats "half as wide" and "twice as wide" equally
      const distance = Math.abs(Math.log(sourceRatio / (size.width / size.height)));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = size;
      }
    });

    return best;
  }

  private upscaleImage(imageUrl: string): Observable<string> {
    if (!this.upscaleModel) {
      return of(imageUrl);
    }

    return this.resolveUpscaleVersion().pipe(
      switchMap(version =>
        this.http.post(
          this.apiUrl('/predictions'),
          {
            version,
            input: {
              image: imageUrl,
              scale: 2,
              face_enhance: false
            }
          },
          httpOptions
        )
      ),
      switchMap((prediction: any) => this.resolvePredictionOutput(prediction))
    );
  }

  private resolvePredictionOutput(prediction: any): Observable<string> {
    if (prediction.status === 'succeeded') {
      return of(this.extractOutputUrl(prediction));
    }
    if (prediction.status === 'failed' || prediction.status === 'canceled') {
      return Observable.throw(prediction.error || new Error('Replicate generation failed'));
    }
    return this.waitForPrediction(prediction.id);
  }

  private resolveUpscaleVersion(): Observable<string> {
    if (this.cachedUpscaleVersionId) {
      return of(this.cachedUpscaleVersionId);
    }

    const [owner, name] = this.upscaleModel.split('/');

    return this.http.get(this.apiUrl(`/models/${owner}/${name}`), httpOptions).pipe(
      map((modelInfo: any) => {
        const versionId = modelInfo && modelInfo.latest_version && modelInfo.latest_version.id;
        if (!versionId) {
          throw new Error(`Replicate model version not found for ${this.upscaleModel}`);
        }
        this.cachedUpscaleVersionId = versionId;
        return versionId;
      })
    );
  }

  private extractOutputUrl(prediction: any): string {
    const output = prediction.output;
    const url = Array.isArray(output) ? output[0] : output;
    if (!url) {
      throw new Error('Replicate returned empty output');
    }
    return url;
  }

  private waitForPrediction(id: string): Observable<string> {
    return new Observable(observer => {
      let cancelled = false;
      let attempts = 0;

      const poll = () => {
        if (cancelled) {
          return;
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          observer.error(new Error('Replicate generation timed out'));
          return;
        }

        attempts++;

        this.http.get(this.apiUrl(`/predictions/${id}`), httpOptions).subscribe(
          (prediction: any) => {
            if (prediction.status === 'succeeded') {
              observer.next(this.extractOutputUrl(prediction));
              observer.complete();
              return;
            }

            if (prediction.status === 'failed' || prediction.status === 'canceled') {
              observer.error(prediction.error || new Error('Replicate generation failed'));
              return;
            }

            setTimeout(poll, POLL_INTERVAL_MS);
          },
          err => observer.error(err)
        );
      };

      poll();

      return () => {
        cancelled = true;
      };
    });
  }

  private fetchAsDataUrl(url: string): Observable<string> {
    if (url.startsWith('data:')) {
      return of(url);
    }

    return this.http.get(this.normalizeImageUrl(url), { responseType: 'blob' }).pipe(
      switchMap(blob => this.blobToDataUrl(blob))
    );
  }

  private blobToDataUrl(blob: Blob): Observable<string> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = () => observer.error(reader.error);
      reader.readAsDataURL(blob);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/replicate-ai-image.service.ts